/**
 * Anonymous Session — localStorage-based state for users who haven't signed up yet.
 * Enables the "try before you sign up" onboarding flow at /try.
 * On signup, data is migrated to Supabase via migrateAnonymousToSupabase().
 */

import * as dataApi from '@/services/dataApi';
import { getCachedUserId } from '@/services/userIdCache';
import { useStore } from '@/store/useStore';
import { reportError } from '@/services/errorTracking';
import { randomUUID } from '@/utils/uuid';
import { FLAGS } from '@/config/featureFlags';
import { getNativeMirror } from '@/lib/native/localStorageMirror';

const SESSION_KEY = 'vidi-anonymous-session';
const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Persist the session and, on native only, mirror it into Capacitor
 * Preferences (plan §2.3). WKWebView localStorage is evictable, and losing
 * this loses every card the user made in /try before signing up.
 *
 * The TTL is unchanged by mirroring: `createdAt` travels inside the value and
 * getAnonymousSession() still expires it on read, so a session recovered from
 * Preferences after 30 days is still discarded.
 */
function persistSession(session: AnonymousSession): void {
  const serialized = JSON.stringify(session);
  localStorage.setItem(SESSION_KEY, serialized);
  if (FLAGS.nativeIOS) getNativeMirror()?.set(SESSION_KEY, serialized);
}

// ─── Security: sanitize migrated data ───────────────────────

function sanitizeString(input: unknown, maxLength = 200): string {
  if (typeof input !== 'string') return '';
  return input
    .replace(/<[^>]*>/g, '')
    .trim()
    .slice(0, maxLength);
}

function validateImageUrl(url: unknown): string {
  if (typeof url !== 'string') return '';
  try {
    const parsed = new URL(url);
    const allowedHosts = [
      'images.pexels.com',
      'images.unsplash.com',
      'pixabay.com',
      'cdn.pixabay.com',
    ];
    if (parsed.protocol !== 'https:' || !allowedHosts.includes(parsed.hostname)) {
      return '';
    }
    return url;
  } catch {
    return '';
  }
}

/** Check if localStorage is available (fails in some private browsing modes) */
export function isStorageAvailable(): boolean {
  try {
    const k = '__vidi_test__';
    localStorage.setItem(k, '1');
    localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

// ─── Types ──────────────────────────────────────────────────

export interface AnonymousCard {
  id: string;
  concept?: string;
  targetWord: string;
  nativeWord: string;
  targetExample: string;
  nativeExample: string;
  wordGender: string | null;
  wordPlural: string | null;
  wordClass: string;
  pronunciationIpa: string;
  imageUrl: string;
  imageThumbnail: string;
  imageSource: string;
  cardType: 'word' | 'phrase' | 'sentence';
  createdAt: string;
  isSample: boolean;
  personalizedImage: boolean;
  /** The learner's own "why this image clicked" hook (deepest memory layer).
      Optional: only flows that ask for a note (redesign landing hero) set it;
      carried onto the real card's personal_note at signup migration. */
  personalNote?: string;
  photoOptions?: {
    id: string;
    url: string;
    thumbnail: string;
    alt: string;
    photographer: string;
  }[];
}

export interface AnonymousSession {
  id: string;
  createdAt: string;
  languagePair: string;
  cefrLevel: string | null;
  nativeLanguage: string;
  targetLanguage: string;
  cards: AnonymousCard[];
  reviewsDone: number;
  onboardingStep: 'language' | 'samples' | 'create' | 'review' | 'signup' | 'complete';
}

// ─── Read / Write ───────────────────────────────────────────

export function getAnonymousSession(): AnonymousSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session: AnonymousSession = JSON.parse(raw);
    // Check TTL
    if (Date.now() - new Date(session.createdAt).getTime() > SESSION_TTL_MS) {
      clearAnonymousSession();
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function hasAnonymousSession(): boolean {
  return getAnonymousSession() !== null;
}

export function createAnonymousSession(
  languagePair: string,
  nativeLanguage: string,
  targetLanguage: string,
): AnonymousSession {
  const session: AnonymousSession = {
    id: randomUUID(),
    createdAt: new Date().toISOString(),
    languagePair,
    cefrLevel: 'A1',
    nativeLanguage,
    targetLanguage,
    cards: [],
    reviewsDone: 0,
    onboardingStep: 'language',
  };
  try {
    persistSession(session);
  } catch (err) {
    reportError(err, { operation: 'createAnonymousSession' });
  }
  return session;
}

export function updateAnonymousSession(updates: Partial<AnonymousSession>): void {
  const session = getAnonymousSession();
  if (!session) return;
  Object.assign(session, updates);
  try {
    persistSession(session);
  } catch (err) {
    reportError(err, { operation: 'updateAnonymousSession' });
  }
}

export function addAnonymousCard(card: AnonymousCard): void {
  const session = getAnonymousSession();
  if (!session) return;
  session.cards.push(card);
  try {
    persistSession(session);
  } catch (err) {
    reportError(err, { operation: 'addAnonymousCard' });
  }
}

export function updateAnonymousCard(cardId: string, updates: Partial<AnonymousCard>): void {
  const session = getAnonymousSession();
  if (!session) return;
  const card = session.cards.find((c) => c.id === cardId);
  if (card) {
    Object.assign(card, updates);
    try {
      persistSession(session);
    } catch (err) {
      reportError(err, { operation: 'updateAnonymousCard' });
    }
  }
}

export function clearAnonymousSession(): void {
  localStorage.removeItem(SESSION_KEY);
  // Without this a native restore would resurrect a session the user already
  // migrated or signed out of.
  if (FLAGS.nativeIOS) getNativeMirror()?.remove(SESSION_KEY);
}

// Native cold-launch restore (plan §2.3). No merge function is passed, so if
// localStorage already holds a session the recovered one is discarded: this
// key is a single self-contained document (not an append-only queue), and the
// local copy is by definition at least as new. Dead code on web.
if (FLAGS.nativeIOS) {
  void getNativeMirror()?.restore([{ key: SESSION_KEY }]);
}

// ─── Migration to Supabase ──────────────────────────────────

type Row = Record<string, unknown>;

/**
 * One run per user at a time. Sign-in loading (DataLoader) and the Try wall's
 * own button can both ask, and two overlapping runs would each insert every
 * card; a second caller simply shares the run already in flight.
 */
const migrationsInFlight = new Map<string, Promise<MigrationResult>>();

/**
 * What a migration run actually did, derived from the durable card rows the backend returned
 * and the selected choices still waiting locally. The public boolean cannot tell these apart
 * and must keep meaning "nothing left waiting".
 *   no_session / nothing_selected: nothing to persist (legacy unselected demos are still cleaned up)
 *   saved: every selected card is durable and at least one row was newly written by this run
 *   already_saved: every selected card was already on the shelf; no new row was written
 *   partial: some selected cards are durable, others stay local for a retry
 *   failed: selected cards remain and none of them is durable
 *   interrupted: the account or the local draft changed under the run; do not call it saved
 * Settings or deck changes alone never count as a card write. A failed store refresh after
 * durable writes is `refreshFailed`, never a save failure.
 */
export type MigrationStatus =
  | 'no_session'
  | 'nothing_selected'
  | 'saved'
  | 'already_saved'
  | 'partial'
  | 'failed'
  | 'interrupted';
export interface MigrationResult {
  status: MigrationStatus;
  /** Same meaning as the boolean: nothing is left waiting locally. */
  complete: boolean;
  wroteCards: boolean;
  refreshFailed: boolean;
  /**
   * Id of the browser's own draft this run imported, read before any local cleanup. Only a local
   * run sets it. DataLoader uses it to acknowledge exactly the matching waiting server copy.
   */
  sourceSessionId?: string;
}
type RunResult = Omit<MigrationResult, 'refreshFailed'> & { changed: boolean };

/**
 * A draft recovered from the server for the signed-in account (Vidi-atl.22.2). It lives only in
 * memory: it is never written to localStorage, so another account on this browser can never
 * pick it up, and a run fed by it never touches this browser's own local draft.
 */
export type RecoveredDraft = Pick<
  AnonymousSession,
  'id' | 'languagePair' | 'cefrLevel' | 'nativeLanguage' | 'targetLanguage' | 'cards'
>;
export interface MigrationOptions {
  /** Account-bound recovered draft. Absent means the browser's own local draft. */
  recovered?: RecoveredDraft;
}

/**
 * Durable import key "<session id>:<card id>", the identity of one selected Try card across the
 * browsers that can import it. Both ids must match the server draft validator's alphabet and
 * 64-character bound (supabase/functions/_shared/tryDraft.ts), so the key is unambiguous, at most
 * 129 characters (the cards.import_key CHECK) and never truncated. Current ids are
 * randomUUID() (36 characters). Null for an older local-only id outside that shape: such a card
 * has no durable key and keeps only the legacy word-pair dedupe (local runs). A recovered card
 * always arrives validated, so null there means a corrupt draft and the card stays unsaved.
 */
const IMPORT_ID = /^[A-Za-z0-9_-]{1,64}$/;
export function buildImportKey(sessionId: string, cardId: string): string | null {
  return IMPORT_ID.test(sessionId) && IMPORT_ID.test(cardId) ? `${sessionId}:${cardId}` : null;
}

/** A card the learner chose: a personal card, or a demo sample they gave their own photo. */
const isSelectedCard = (c: AnonymousCard) => !c.isSample || c.personalizedImage;

/** True when a draft holds at least one selected card (an actual import, not demo cleanup). */
export function hasSelectedAnonymousCards(): boolean {
  return getAnonymousSession()?.cards.some(isSelectedCard) ?? false;
}

/**
 * Move the visitor's /try choices into their account.
 *
 * Resolves true when nothing is left waiting (no local session, or every
 * chosen card is saved), false when something must be retried. It never
 * rejects for a backend failure: a failed step is reported and the local
 * choices stay on this device, because they are the only copy until the
 * account has them. Only cards that are saved (or already on the shelf) leave
 * the local session; it is cleared only when nothing remains.
 *
 * Nothing here may mark a card as the learner's own. The origin is only
 * 'starter' (known demo content) or unknown: a /try card never proves the
 * learner came up with the word themselves, and that claim drives the
 * first-card activation count.
 */
export function migrateAnonymousToSupabase(userId: string): Promise<boolean> {
  return migrateAnonymousDetailed(userId).then((result) => result.complete);
}

/** Same single in-flight run per user as the boolean wrapper; callers share one result. */
export function migrateAnonymousDetailed(
  userId: string,
  options: MigrationOptions = {},
): Promise<MigrationResult> {
  // A recovered run is separate work from the local draft's run: sharing one promise would
  // hand a caller the other source's result (and its local cleanup decision).
  const flightKey = options.recovered ? `recovered:${userId}` : userId;
  const running = migrationsInFlight.get(flightKey);
  if (running) return running;

  const run = (async () => {
    const { changed, ...result } = await runMigration(userId, options.recovered);
    let refreshFailed = false;
    // Anything durable (even on a partial failure) makes the loaded store stale.
    if (changed) {
      try {
        await useStore.getState().refreshFromApi();
      } catch (err) {
        // The data is saved; whoever loads the store next will surface a load error.
        refreshFailed = true;
        reportError(err, { operation: 'migrateAnonymous:refresh' });
      }
    }
    return { ...result, refreshFailed };
  })().finally(() => migrationsInFlight.delete(flightKey));
  migrationsInFlight.set(flightKey, run);
  return run;
}

/** Existing shelf pairs inside the demo language pair or the chosen deck only. */
async function fetchShelfPairs(languagePair: string, deckId: string): Promise<Set<string>> {
  const pairs = new Set<string>();
  for (const raw of await dataApi.fetchCards()) {
    const row = raw as Row;
    const inPair = (row.language_pair ?? row.languagePair) === languagePair;
    const inDeck = (row.deck_id ?? row.deckId) === deckId;
    if (!inPair && !inDeck) continue;
    const target = String(row.target_word ?? row.targetWord ?? '')
      .trim()
      .toLowerCase();
    const native = String(row.native_word ?? row.nativeWord ?? '')
      .trim()
      .toLowerCase();
    if (target && native) pairs.add(`${target}|${native}`);
  }
  return pairs;
}

async function runMigration(userId: string, recovered?: RecoveredDraft): Promise<RunResult> {
  let changed = false;
  let wroteCards = false;
  const session = recovered ?? getAnonymousSession();
  if (!session) return { status: 'no_session', complete: true, changed, wroteCards };
  const sourceSessionId = recovered ? undefined : session.id;
  const finish = (status: MigrationStatus, complete: boolean): RunResult => ({
    status,
    complete,
    changed,
    wroteCards,
    sourceSessionId,
  });
  try {
    if ((await getCachedUserId()) !== userId) return finish('interrupted', false);
  } catch (err) {
    reportError(err, { operation: 'migrateAnonymous:account' });
    return finish('failed', false);
  }

  // Step 1: Settings. A brand-new account gets the demo pair + CEFR so it
  // isn't stuck in onboarding. An already-onboarded account must keep its
  // pair and level — /try after sign-in only donates cards.
  let alreadyOnboarded = false;
  try {
    const existing = (await dataApi.fetchUserSettings()) as Row | null;
    alreadyOnboarded = Boolean(existing?.onboarding_completed ?? existing?.onboardingCompleted);
  } catch (err) {
    reportError(err, { operation: 'migrateAnonymous:fetchSettings' });
    // Guessing "not onboarded" here would overwrite a real account's pair and level.
    return finish('failed', false);
  }

  if (!alreadyOnboarded) {
    try {
      await dataApi.updateUserSettings(
        {
          currentLanguagePair: session.languagePair,
          cefrLevel: session.cefrLevel,
          onboardingCompleted: true,
        },
        userId,
      );
      changed = true;
    } catch (err) {
      reportError(err, { operation: 'migrateAnonymous:settings' });
      return finish('failed', false);
    }
  }

  // Step 2: The deck and the shelf's existing pairs. Both must be known before
  // inserting: a wrong deck puts words under another language, and without the
  // pair list a retry would insert duplicates.
  let deckId: string;
  let shelfPairs: Set<string>;
  try {
    const decks = await dataApi.fetchDecks();
    // Only a deck of the demo's own language pair may receive these cards.
    const sameLanguage = (Array.isArray(decks) ? (decks as Row[]) : []).filter(
      (deck) => (deck.language_pair ?? deck.languagePair) === session.languagePair,
    );
    const existingDeck =
      sameLanguage.find((deck) => Boolean(deck.is_default ?? deck.isDefault)) ?? sameLanguage[0];
    if (existingDeck) {
      deckId = String(existingDeck.id);
    } else {
      const created = (await dataApi.createDeck(
        {
          name: `${session.targetLanguage || 'Vocabulary'} Vocabulary`,
          emoji: '📚',
          languagePair: session.languagePair,
          isDefault: true,
        },
        userId,
      )) as Row | null;
      if (typeof created?.id !== 'string' || !created.id) {
        throw new Error('Deck was created without an id');
      }
      deckId = created.id;
      changed = true;
    }
    // A recovered card is identified by its exact import key alone, never by word pair: an
    // unrelated card with the same word and another photo or note is not this import.
    shelfPairs = recovered ? new Set() : await fetchShelfPairs(session.languagePair, deckId);
  } catch (err) {
    reportError(err, { operation: 'migrateAnonymous:deck' });
    return finish('failed', false);
  }

  // Step 3: Each chosen card on its own, so one failure keeps only that card.
  const unsavedIds = new Set<string>();
  const cardsToMigrate = session.cards.filter(isSelectedCard);
  // Selected cards that are durable after this run: newly written or already on the shelf.
  let durableCount = 0;
  for (const card of cardsToMigrate) {
    try {
      const targetWord = sanitizeString(card.targetWord, 200);
      const nativeWord = sanitizeString(card.nativeWord, 200);
      const pairKey = `${targetWord.toLowerCase()}|${nativeWord.toLowerCase()}`;
      // Local runs keep the legacy rule: an earlier visit or a hand-made card with the same word
      // pair counts as already saved. The existing card is never overwritten.
      if (!recovered && shelfPairs.has(pairKey)) {
        durableCount += 1;
        continue;
      }
      const safeImageUrl = validateImageUrl(card.imageUrl);
      // Durable duplicate guard shared by every source of this card (this browser, a recovered
      // copy, another tab): the database refuses a second row with the same key.
      const importKey = buildImportKey(session.id, card.id);
      if (recovered && !importKey) throw new Error('Recovered card has no usable import key');
      const saved = (await dataApi.createCard(
        {
          targetWord,
          nativeWord,
          targetExample: sanitizeString(card.targetExample, 500),
          nativeExample: sanitizeString(card.nativeExample, 500),
          wordGender: sanitizeString(card.wordGender, 20) || null,
          wordPlural: sanitizeString(card.wordPlural, 100) || null,
          wordClass: sanitizeString(card.wordClass, 30),
          pronunciationIpa: sanitizeString(card.pronunciationIpa, 100),
          imageUrl: safeImageUrl,
          imageThumbnail: validateImageUrl(card.imageThumbnail) || safeImageUrl,
          imageSource: sanitizeString(card.imageSource, 50),
          deckId,
          // Known demo content is 'starter'. Older sessions saved false/missing
          // metadata (the old hero card said false for a fixed demo word), so
          // those stay unknown rather than being credited to the learner.
          cardOrigin: card.isSample === true ? 'starter' : null,
          languagePair: session.languagePair,
          cardType:
            card.cardType === 'phrase'
              ? 'phrase'
              : card.cardType === 'sentence'
                ? 'sentence'
                : 'word',
          personalNote: sanitizeString(card.personalNote, 500) || null,
          isIrregular: false,
          importKey,
        },
        userId,
      )) as Row | null;
      // A save only counts when the database handed back the saved row's id.
      if (typeof saved?.id !== 'string' || !saved.id) {
        throw new Error('Card was saved without a returned id');
      }
      shelfPairs.add(pairKey);
      durableCount += 1;
      // A unique-key conflict returned the durable row from an earlier import of this card.
      if (saved.alreadyImported === true) continue;
      changed = true;
      wroteCards = true;
    } catch (err) {
      unsavedIds.add(card.id);
      reportError(err, { operation: 'migrateAnonymous:card', extra: { word: card.targetWord } });
    }
  }

  // A recovered draft has no local copy to forget: the server copy is acknowledged by the
  // caller only after a complete result.
  if (recovered) {
    const settledRecovered: MigrationStatus =
      cardsToMigrate.length === 0
        ? 'nothing_selected'
        : unsavedIds.size === 0
          ? wroteCards
            ? 'saved'
            : 'already_saved'
          : durableCount > 0
            ? 'partial'
            : 'failed';
    try {
      if ((await getCachedUserId()) !== userId) return finish('interrupted', false);
    } catch (err) {
      reportError(err, { operation: 'migrateAnonymous:account' });
      return finish('interrupted', false);
    }
    return finish(settledRecovered, unsavedIds.size === 0);
  }

  // Step 4: Forget only what is safe. Everything above awaited the network, so
  // the learner may have changed the local session meanwhile (new card, new
  // photo, a whole new session) or the account may have changed.
  // Snapshot of every original card this run made safe to forget: saved,
  // already durable, or an unselected demo sample. Keyed by id but compared by
  // content: an id match alone would also delete a card whose photo or note was
  // edited after we read it, losing the learner's newer choice.
  const safeSnapshots = new Map<string, string>();
  for (const card of session.cards) {
    if (!unsavedIds.has(card.id)) safeSnapshots.set(card.id, JSON.stringify(card));
  }

  // What the selected cards alone say, before any later interruption is considered.
  const settled: MigrationStatus =
    cardsToMigrate.length === 0
      ? 'nothing_selected'
      : unsavedIds.size === 0
        ? wroteCards
          ? 'saved'
          : 'already_saved'
        : durableCount > 0
          ? 'partial'
          : 'failed';
  // Every selected card was safe, so anything still unsafe is the learner's newer edit.
  const interruptedIfAllSafe: MigrationStatus = unsavedIds.size === 0 ? 'interrupted' : settled;
  const settledIfAllSafe: MigrationStatus = unsavedIds.size === 0 ? settled : 'interrupted';

  // Fresh account check, then reread the session with no await in between so
  // nothing can change it before cleanup.
  try {
    if ((await getCachedUserId()) !== userId) return finish(interruptedIfAllSafe, false);
  } catch (err) {
    reportError(err, { operation: 'migrateAnonymous:account' });
    return finish(interruptedIfAllSafe, false);
  }
  const latest = getAnonymousSession();
  if (!latest) return finish(settledIfAllSafe, true);
  // A different session is the learner's newer work; it is not ours to touch.
  if (latest.id !== session.id || latest.languagePair !== session.languagePair) {
    return finish('interrupted', false);
  }
  const remaining = latest.cards.filter((c) => safeSnapshots.get(c.id) !== JSON.stringify(c));
  if (remaining.length === 0) {
    clearAnonymousSession();
    return finish(settledIfAllSafe, true);
  }
  updateAnonymousSession({ cards: remaining });
  return finish(interruptedIfAllSafe, false);
}

/**
 * The selected cards (at most 5) of this browser's draft, shaped for signup to park server-side.
 * Unselected demo cards and photoOptions never leave the device; the edge function validates
 * everything again. Null when nothing is selected.
 */
export function buildPendingTryDraft(): Record<string, unknown> | null {
  const session = getAnonymousSession();
  const cards = session?.cards.filter(isSelectedCard).slice(0, 5) ?? [];
  if (!session || cards.length === 0) return null;
  return {
    sessionId: session.id,
    languagePair: session.languagePair,
    cefrLevel: session.cefrLevel,
    cards: cards.map((c) => ({
      id: c.id,
      targetWord: c.targetWord,
      nativeWord: c.nativeWord,
      targetExample: c.targetExample,
      nativeExample: c.nativeExample,
      wordGender: c.wordGender,
      wordPlural: c.wordPlural,
      wordClass: c.wordClass,
      pronunciationIpa: c.pronunciationIpa,
      imageUrl: validateImageUrl(c.imageUrl),
      imageThumbnail: validateImageUrl(c.imageThumbnail),
      imageSource: c.imageSource,
      cardType: c.cardType,
      isSample: c.isSample,
      personalizedImage: c.personalizedImage,
      personalNote: c.personalNote ?? null,
    })),
  };
}
